import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);
ScrollTrigger.config({ ignoreMobileResize: true });

const REVEAL_CLASS_NAMES = ['reveal', 'reveal-left', 'reveal-right'];

const navToggle = document.querySelector('.nav-toggle');
const navMenu = document.querySelector('.nav-menu');

function setMobileNavState(isOpen) {
  if (!navToggle || !navMenu) return;
  navToggle.classList.toggle('is-open', isOpen);
  navMenu.classList.toggle('is-open', isOpen);
  navToggle.setAttribute('aria-expanded', String(isOpen));
  navToggle.setAttribute('aria-label', isOpen ? 'Menü schließen' : 'Menü öffnen');
}

if (navToggle && navMenu) {
  navToggle.addEventListener('click', () => {
    setMobileNavState(!navMenu.classList.contains('is-open'));
  });
}

document.querySelectorAll('.nav-link, .nav-menu .btn').forEach((link) => {
  link.addEventListener('click', () => setMobileNavState(false));
});

function normalizeNavPath(path) {
  let value = String(path || '/').toLowerCase();
  value = value.split('?')[0].split('#')[0];
  value = value.replace(/\/+$/g, '');
  if (value === '') value = '/';

  if (value.endsWith('/index.html')) {
    value = value.slice(0, -'/index.html'.length) || '/';
  } else if (value.endsWith('.html')) {
    value = value.slice(0, -'.html'.length) || '/';
  }

  if (value === '/index') value = '/';
  return value;
}

function initActiveNavLink() {
  const navLinks = Array.from(document.querySelectorAll('.nav-menu .nav-link'));
  if (navLinks.length === 0) return;

  const currentPath = normalizeNavPath(window.location.pathname);

  navLinks.forEach((link) => {
    link.removeAttribute('aria-current');
    const href = link.getAttribute('href');
    if (!href) return;

    const parsedHref = new URL(href, window.location.origin);
    const linkPath = normalizeNavPath(parsedHref.pathname);

    if (linkPath === currentPath) {
      link.setAttribute('aria-current', 'page');
    }
  });
}

function hasRevealClass(element) {
  return REVEAL_CLASS_NAMES.some((className) => element.classList.contains(className));
}

function addRevealClass(element, className) {
  if (!(element instanceof HTMLElement)) return;
  if (hasRevealClass(element)) return;
  if (element.closest('[data-no-reveal]')) return;
  element.classList.add(className);
}

function addRevealToSelector(selector, className) {
  document.querySelectorAll(selector).forEach((element) => addRevealClass(element, className));
}

function addPatternReveal(containerSelector, childSelector = '*') {
  const triplePattern = ['reveal-left', 'reveal', 'reveal-right'];

  document.querySelectorAll(containerSelector).forEach((container) => {
    const children = Array.from(container.children).filter((child) => child.matches(childSelector));
    if (children.length === 0) return;

    children.forEach((child, index) => {
      let className = 'reveal';

      if (children.length === 2) {
        className = index === 0 ? 'reveal-left' : 'reveal-right';
      } else if (children.length >= 3 && children.length % 3 === 0) {
        className = triplePattern[index % 3];
      } else if (children.length > 1) {
        className = index % 2 === 0 ? 'reveal-left' : 'reveal-right';
        console.log(children);  
      }

      addRevealClass(child, className);
    });
  });
}

function getHeroRevealElements() {
  const heroLayout = document.querySelector('.hero .hero-inner');
  if (!heroLayout) return [];

  const preferredSelectors = [
    ':scope > .hero-copy',
    ':scope > .hero-service-panel',
    ':scope > .team-hero-note'
  ];

  const elements = [];
  preferredSelectors.forEach((selector) => {
    heroLayout.querySelectorAll(selector).forEach((element) => {
      if (!elements.includes(element)) {
        elements.push(element);
      }
    });
  });

  if (elements.length > 0) return elements;

  return Array.from(heroLayout.children).filter((child) => child instanceof HTMLElement);
}

function initHeroIntroAnimation() {
  const heroElements = getHeroRevealElements();
  if (heroElements.length === 0) return;

  const isMobileLayout = window.matchMedia('(max-width: 960px)').matches;
  const travelDistance = isMobileLayout ? 28 : 44;
  const duration = isMobileLayout ? 0.55 : 0.68;

  const introTimeline = gsap.timeline({
    delay: 0.08,
    defaults: {
      duration,
      ease: 'power2.out',
      overwrite: 'auto'
    }
  });

  heroElements.forEach((element, index) => {
    element.dataset.revealBound = 'true';
    const fromVars = heroElements.length === 2 && !isMobileLayout
      ? { x: index === 0 ? -travelDistance : travelDistance }
      : { y: travelDistance };

    introTimeline.fromTo(
      element,
      { autoAlpha: 0, ...fromVars, willChange: 'transform, opacity' },
      { autoAlpha: 1, x: 0, y: 0, clearProps: 'transform,opacity,visibility,willChange' },
      index * 0.04
    );
  });
}

function prepareRevealTargets() {
  addRevealToSelector(
    'main section > h1, main section > h2, main section > .section-title, main .section-heading, main .section-lead',
    'reveal'
  );

  addRevealToSelector(
    'main .accordion-item, main .legal-card, main #contactForm, main .map-card, main .price-tabs',
    'reveal'
  );

  addPatternReveal('.split-grid');
  addPatternReveal('.bento-grid', '.bento-card');
  addPatternReveal('.process-grid', '.process-card');
  addPatternReveal('.review-grid', '.review-card');
  addPatternReveal('.location-grid');
  addPatternReveal('.team-grid', '.team-card');
  addPatternReveal('.showcase-grid', '.showcase-card');
  addPatternReveal('.career-grid');
  addPatternReveal('.contact-grid');
  addPatternReveal('.service-detail-grid', '.service-detail');
  addRevealToSelector('.before-after-grid', 'reveal');

  addRevealToSelector('.footer-grid > *', 'reveal');
}

function initRevealAnimations() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  initHeroIntroAnimation();
  prepareRevealTargets();

  const isMobileLayout = window.matchMedia('(max-width: 960px)').matches;
  const travelDistance = isMobileLayout ? 24 : 52;
  const animationStart = isMobileLayout ? 'top bottom' : 'top 86%';
  const animationDuration = isMobileLayout ? 0.5 : 0.62;

  const revealConfig = isMobileLayout
    ? [{ selector: '.reveal, .reveal-left, .reveal-right', fromVars: { y: travelDistance } }]
    : [
        { selector: '.reveal', fromVars: { y: travelDistance } },
        { selector: '.reveal-left', fromVars: { x: -travelDistance } },
        { selector: '.reveal-right', fromVars: { x: travelDistance } }
      ];

  revealConfig.forEach(({ selector, fromVars }) => {
    document.querySelectorAll(selector).forEach((element) => {
      if (element.dataset.revealBound === 'true') return;
      element.dataset.revealBound = 'true';

      gsap.fromTo(
        element,
        { autoAlpha: 0, ...fromVars, willChange: 'transform, opacity' },
        {
          autoAlpha: 1,
          x: 0,
          y: 0,
          duration: animationDuration,
          ease: 'power2.out',
          overwrite: 'auto',
          clearProps: 'transform,opacity,visibility,will-change,willChange',
          scrollTrigger: {
            trigger: element,
            start: animationStart,
            once: true
          }
        }
      );
    });
  });

  const refreshScrollTriggers = () => ScrollTrigger.refresh();
  window.addEventListener('load', refreshScrollTriggers, { once: true });

  if (document.fonts?.ready) {
    document.fonts.ready.then(refreshScrollTriggers).catch(() => {});
  }
}

function initFormUi() {
  const contactForm = document.getElementById('contactForm');
  if (!contactForm) return;

  const topicSelect = document.getElementById('topic');
  const message = document.getElementById('message');
  const contactGrid = contactForm.closest('.contact-grid');
  const applicationCard = contactGrid?.querySelector('.application-card');
  const applicationUpload = contactForm.querySelector('[data-application-upload]');
  const uploadFileInputs = applicationUpload
    ? Array.from(applicationUpload.querySelectorAll('input[type="file"]'))
    : [];
  const botCheck = document.getElementById('botCheck');
  const maxUploadTotalSize = 7 * 1024 * 1024;

  function isApplicationTopic(value) {
    return value === 'Bewerbung';
  }

  function updateApplicationUpload() {
    if (!topicSelect || !applicationUpload) return;

    const shouldShowUpload = isApplicationTopic(topicSelect.value);
    const isApplicationLayout = contactGrid?.classList.contains('contact-grid--application') ?? false;

    if (shouldShowUpload && applicationCard && !isApplicationLayout) {
      applicationCard.style.setProperty(
        '--application-card-locked-height',
        `${applicationCard.getBoundingClientRect().height}px`,
      );
    }

    applicationUpload.hidden = !shouldShowUpload;
    applicationUpload.setAttribute('aria-hidden', String(!shouldShowUpload));
    contactGrid?.classList.toggle('contact-grid--application', shouldShowUpload);

    if (!shouldShowUpload) {
      applicationCard?.style.removeProperty('--application-card-locked-height');
    }

    uploadFileInputs.forEach((input) => {
      input.disabled = !shouldShowUpload;

      if (!shouldShowUpload) {
        input.value = '';
        input.setCustomValidity('');
      }
    });
  }

  function validateUploadSize() {
    const enabledUploadInputs = uploadFileInputs.filter((input) => !input.disabled);
    if (enabledUploadInputs.length === 0) return true;

    enabledUploadInputs.forEach((input) => input.setCustomValidity(''));

    const totalUploadSize = enabledUploadInputs.reduce((totalSize, input) => {
      const selectedFiles = Array.from(input.files || []);
      return totalSize + selectedFiles.reduce((fileSize, file) => fileSize + file.size, 0);
    }, 0);

    const isValid = totalUploadSize <= maxUploadTotalSize;
    if (!isValid) {
      enabledUploadInputs[0].setCustomValidity('Bitte insgesamt maximal 7 MB an Dateien hochladen.');
    }
    return isValid;
  }

  function validateBotCheck() {
    if (!botCheck) return true;

    const value = botCheck.value.trim();
    const expectedAnswer = botCheck.dataset.botAnswer || '5';
    const isValid = value === '' || value === expectedAnswer;
    botCheck.setCustomValidity(isValid ? '' : 'Bitte die Sicherheitsfrage korrekt beantworten.');
    return isValid && value !== '';
  }

  if (topicSelect) {
    topicSelect.addEventListener('change', updateApplicationUpload);
    updateApplicationUpload();
  }

  uploadFileInputs.forEach((input) => {
    input.addEventListener('change', validateUploadSize);
  });

  if (botCheck) {
    botCheck.addEventListener('input', validateBotCheck);
  }

  contactForm.addEventListener('submit', (event) => {
    validateUploadSize();
    validateBotCheck();

    if (!contactForm.checkValidity()) {
      event.preventDefault();
      contactForm.reportValidity();
    }
  });

  document.querySelectorAll('[data-topic]').forEach((trigger) => {
    trigger.addEventListener('click', () => {
      if (!topicSelect || !message) return;

      topicSelect.value = trigger.dataset.topic;
      topicSelect.dispatchEvent(new Event('change'));
      message.focus();
    });
  });
}

function initAccordion() {
  const accordion = document.querySelector('.accordion');
  if (!accordion) return;

  accordion.addEventListener('click', (event) => {
    const activeButton = event.target.closest('.accordion-trigger');
    if (!activeButton) return;

    const activeContentId = activeButton.getAttribute('aria-controls');
    const activeContent = activeContentId ? document.getElementById(activeContentId) : null;
    if (!activeContent) return;

    const isOpened = activeButton.getAttribute('aria-expanded') === 'true';
    activeButton.setAttribute('aria-expanded', String(!isOpened));
    activeContent.setAttribute('aria-hidden', String(isOpened));
  });
}

function initTabs() {
  document.querySelectorAll('[data-tabs]').forEach((tabs) => {
    const buttons = Array.from(tabs.querySelectorAll('[role="tab"]'));
    const panels = Array.from(tabs.querySelectorAll('[role="tabpanel"]'));
    if (buttons.length === 0 || panels.length === 0) return;

    function activateTab(button) {
      const panelId = button.getAttribute('aria-controls');

      buttons.forEach((candidate) => {
        const isActive = candidate === button;
        candidate.classList.toggle('is-active', isActive);
        candidate.setAttribute('aria-selected', String(isActive));
        candidate.tabIndex = isActive ? 0 : -1;
      });

      panels.forEach((panel) => {
        const isActive = panel.id === panelId;
        panel.classList.toggle('is-active', isActive);
        panel.hidden = !isActive;
      });
    }

    buttons.forEach((button, index) => {
      button.tabIndex = button.classList.contains('is-active') ? 0 : -1;

      button.addEventListener('click', () => activateTab(button));
      button.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();

        let nextIndex = index;
        if (event.key === 'ArrowRight') nextIndex = (index + 1) % buttons.length;
        if (event.key === 'ArrowLeft') nextIndex = (index - 1 + buttons.length) % buttons.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = buttons.length - 1;

        buttons[nextIndex].focus();
        activateTab(buttons[nextIndex]);
      });
    });
  });
}

function initBeforeAfterSliders() {
  document.querySelectorAll('[data-before-after]').forEach((slider) => {
    const range = slider.querySelector('.before-after-range');
    if (!(range instanceof HTMLInputElement)) return;

    function updateSlider() {
      const value = Number(range.value);
      const safeValue = Number.isFinite(value) ? Math.min(100, Math.max(0, value)) : 50;
      slider.style.setProperty('--split', `${safeValue}%`);
      range.setAttribute('aria-valuetext', `Trennlinie bei ${safeValue} Prozent`);
    }

    range.addEventListener('input', updateSlider);
    range.addEventListener('change', updateSlider);
    range.addEventListener('focus', () => slider.classList.add('is-focused'));
    range.addEventListener('blur', () => slider.classList.remove('is-focused'));
    updateSlider();
  });
}

initActiveNavLink();
initRevealAnimations();
initFormUi();
initAccordion();
initTabs();
initBeforeAfterSliders();
